
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Supporting non-Plaid traffic',
  layout: 'guide',
  description: 'Run Plaid’s user-scoped access token architecture alongside per-app tokens for other aggregators',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Supporting non-Plaid traffic' subtitle={metadata.description} mdxType="Header" />
      <p>{`Core Exchange asks you to issue Plaid a single `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/planning#architecture-and-token-model"
        }}>{`user-scoped access token`}</a>{` per user. Other aggregators and third parties may ask for the opposite: a separate token per downstream app. Both patterns can be used against the same FDX-compatible API, so you don't need to choose one client model for every caller.`}</p>
      <p>{`This guide covers how to easily support both patterns.`}</p>
      <h2 {...{
        "id": "why-the-two-patterns-differ"
      }}>{`Why the two patterns differ`}</h2>
      <p>{`Plaid's user-scoped access token reduces the number of tokens your authorization server issues and the number of requests your resource server serves: one token covers every app a user connects through Plaid, instead of one token per app. See `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/planning#architecture-and-token-model"
        }}>{`Planning your integration`}</a>{` for the traffic and consent-management benefits.`}</p>
      <p>{`Most other aggregators haven't adopted that model. They register each downstream app as its own OAuth client, the model `}<a parentName="p" {...{
          "href": "https://datatracker.ietf.org/doc/html/rfc7591"
        }}>{`RFC 7591`}</a>{` (Dynamic Client Registration) describes, and so end up with a token per app.`}</p>
      <h2 {...{
        "id": "supporting-both-patterns-on-one-api"
      }}>{`Supporting both patterns on one API`}</h2>
      <Callout mdxType="Callout">
        <p>{`This table is a high-level comparison, not a spec. Check the relevant aggregator's own documentation for their exact requirements.`}</p>
      </Callout>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}></th>
            <th parentName="tr" {...{
              "align": null
            }}>{`For Plaid`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`For other aggregators`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Client registration`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Issue Plaid one client ID and secret, used on behalf of every app connected through Plaid. `}<a parentName="td" {...{
                "href": "/core-exchange/docs/authentication/planning#app-directory-recommended"
              }}>{`App Directory`}</a>{` gives you app-level visibility in place of registration.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Issue a client ID and secret per downstream app, registered through your Dynamic Client Registration endpoint.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`OAuth user experience`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Plaid Link handles consent, data-type scoping, and account selection for Plaid traffic, so these screens should be suppressed when the caller is Plaid. Suppress them by branching on Plaid's `}<inlineCode parentName="td">{`client_id`}</inlineCode>{`, which is sent on every `}<inlineCode parentName="td">{`/authorize`}</inlineCode>{` request.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Show whatever consent, scoping, and account-selection screens you'd otherwise show.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Authorization record creation`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Plaid is the source of truth for who's authorized. Subscribe to the `}<a parentName="td" {...{
                "href": "/core-exchange/docs/consent-management/api#authorization_granted"
              }}><inlineCode parentName="a">{`AUTHORIZATION_GRANTED`}</inlineCode>{` webhook`}</a>{` to be notified when a user connects a new app, then record that connection in your own consent records.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`After the end user completes your OAuth flow, store the authorization record directly in your own consent records.`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><strong parentName="td">{`Ongoing authorization management`}</strong></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Use Plaid's `}<a parentName="td" {...{
                "href": "/core-exchange/docs/consent-management/api"
              }}>{`Consent API`}</a>{` to see which apps a user has connected through Plaid, and to process revocations.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Query your own consent records for a user's connected apps, and revoke the refresh token for that client ID/user directly.`}</td>
          </tr>
        </tbody>
      </table>
      <h2 {...{
        "id": "identifying-the-caller"
      }}>{`Identifying the caller`}</h2>
      <p>{`In order to support both models, your integration must be able to tell Plaid's traffic apart from everyone else's at the point a request comes in. The most reliable signal is `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`: register Plaid under a single, known client ID, and branch your OAuth and consent-record logic on whether the request came from it. If every other caller registers through Dynamic Client Registration, Plaid is your only manually registered client, so you can already tell them apart.`}</p>
      <h2 {...{
        "id": "dynamic-client-registration"
      }}>{`Dynamic Client Registration`}</h2>
      <p>{`Even though Plaid does not require Dynamic Client Registration, if you serve other aggregators or third parties that register each downstream app as its own client, you will still need to implement Dynamic Client Registration to support those clients.`}</p>
      <h2 {...{
        "id": "related-reading"
      }}>{`Related reading`}</h2>
      <ul>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/authentication/planning"
          }}>{`Planning your integration`}</a>{` — the user-scoped access token architecture and App Directory, in depth`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/permissions-manager"
          }}>{`App Directory`}</a>{` — how Plaid gives you visibility into connected apps without Dynamic Client Registration`}</li>
        <li parentName="ul"><a parentName="li" {...{
            "href": "/core-exchange/docs/consent-management/api"
          }}>{`Consent API`}</a>{` — querying and revoking authorizations for Plaid traffic`}</li>
      </ul>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "why-the-two-patterns-differ",
        level: 2,
        title: "Why the two patterns differ",
      },
{
        id: "supporting-both-patterns-on-one-api",
        level: 2,
        title: "Supporting both patterns on one API",
      },
{
        id: "identifying-the-caller",
        level: 2,
        title: "Identifying the caller",
      },
{
        id: "dynamic-client-registration",
        level: 2,
        title: "Dynamic Client Registration",
      },
{
        id: "related-reading",
        level: 2,
        title: "Related reading",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
